import { GROUP_HEADER_HEIGHT } from "../settings.ts";
import type { RangePerContig } from "../store/slices/range-selection-slice.ts";
import { useGenomeViewerStore } from "../store/store.ts";

interface InitializeResult {
  basePerPixel: number;
  basesOnScreen: number;
  coordinateInterval: number;
  ctx: CanvasRenderingContext2D;
  endBase: number;
  firstCoord: number;
  scrollX: number;
  startBase: number;
  width: number;
}

/**
 * Using floor() instead of round() because:
 * - For genomic data, you typically want positions to align consistently to the left.
 * - When drawing features with widths, using floor ensures consistent spacing and prevents pixel rounding errors from accumulating.
 */
export function initRenderBaseMessageData(baseShift = 0): InitializeResult {
  const state = useGenomeViewerStore.getState();
  const ctx = state.ctx!;
  const width = state.canvas?.width ?? 5;
  const basePerPixel = state.basePerPixel;
  const scrollX = state.scrollX;

  const startBase = Math.floor(scrollX * basePerPixel - baseShift);
  const endBase = Math.ceil((scrollX + width) * basePerPixel - baseShift);
  const basesOnScreen = width * basePerPixel;

  // Calculate interval for coordinates based on screen width
  const coordinateInterval = getCoordinateInterval(width, basesOnScreen);

  // Use bitwise operations for faster integer division
  const firstCoord = Math.floor(startBase / coordinateInterval) * coordinateInterval;

  return {
    basePerPixel,
    basesOnScreen,
    coordinateInterval,
    ctx,
    endBase,
    firstCoord,
    scrollX,
    startBase,
    width,
  };
}

export const getAllTracksHeight = (): number => {
  const { trackGroups, tracks } = useGenomeViewerStore.getState();
  let totalHeight = 0;

  for (const [groupId, group] of trackGroups) {
    if (!group.graphics.isVisible) continue;

    totalHeight += GROUP_HEADER_HEIGHT;

    for (const track of tracks.values()) {
      if (track.groupId === groupId && track.isVisible) {
        totalHeight += track.height;
      }
    }
  }

  return totalHeight;
};

export function getCoordinateInterval(width: number, basesOnScreen: number): number {
  const baseIntervals = [1, 2, 5, 10, 20, 50];
  const targetTicks = Math.floor(width / 90) ?? 1;
  let interval = 1;

  while (basesOnScreen / interval > targetTicks) {
    interval *= 10;
  }

  for (let i = baseIntervals.length - 1; i >= 0; i--) {
    if (basesOnScreen / (interval / baseIntervals[i]) <= targetTicks) {
      interval /= baseIntervals[i];
      break;
    }
  }

  return Math.max(5, interval);
}

export function getMaxRangeLength(rangeSelections: RangePerContig[]): number {
  return Math.max(...rangeSelections.map((range) => range.stop - range.start + 1));
}
